import React, { createContext, useContext, useState, useCallback } from 'react';
import { Currency, CurrencyContextType } from './types';
import { DEFAULT_CURRENCY, STORAGE_KEY } from './currency-config';
import { useValueLists } from '@common/http/value-lists';

// Get initial currency from localStorage or use default
function getInitialCurrency(): Currency {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Currency | null;
    return saved || DEFAULT_CURRENCY;
  } catch {
    return DEFAULT_CURRENCY;
  }
}

// Create context
const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for currency context
 */
export function CurrencyProvider({ children }: CurrencyProviderProps) {
  const [currency, setCurrencyState] = useState<Currency>(getInitialCurrency);
  const { data: valueLists } = useValueLists(['currencies']);
  const availableCurrencies = valueLists?.currencies || {};

  // Save to localStorage when currency changes
  const setCurrency = useCallback((newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    try {
      localStorage.setItem(STORAGE_KEY, newCurrency);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, availableCurrencies }}>
      {children}
    </CurrencyContext.Provider>
  );
}

/**
 * Hook to use currency context
 */
export function useCurrency(): CurrencyContextType {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
}
